import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Menu, ArrowUp, ArrowRight, Clock, Users as UsersIcon } from "lucide-react";
import { Link } from "react-router-dom";
import "./Terms.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TEAL = "#00D1C1";

const TermsSection = ({ id, number, title, children }) => (
  <section id={id} className="terms-section">
    <div className="terms-section-inner">
      <div className="terms-section-heading">
        <div className="terms-section-number">{number}</div>
        <h2 className="terms-section-title">{title}</h2>
      </div>
      <div className="terms-section-body">{children}</div>
    </div>
  </section>
);

const Terms = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [faqOpen, setFaqOpen] = useState({ 0: true, 1: false });

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  const navItems = [
    { id: "introduction", label: "Introduction" },
    { id: "eligibility", label: "Eligibility & Accounts" },
    { id: "service", label: "Service Description" },
    { id: "work-tracking", label: "Work Tracking & Privacy" },
    { id: "subscriptions", label: "Subscriptions & Payments" },
    { id: "admin", label: "Admin Responsibilities" },
    { id: "acceptable-use", label: "Acceptable Use" },
    { id: "intellectual-property", label: "Intellectual Property" },
    { id: "google-api", label: "Google API & Email Insights" },
    { id: "third-party", label: "Third-Party Services" },
    { id: "warranties", label: "Disclaimer of Warranties" },
    { id: "liability", label: "Limitation of Liability" },
    { id: "indemnification", label: "Indemnification" },
    { id: "termination", label: "Suspension & Termination" },
    { id: "changes", label: "Changes to Terms" },
    { id: "governing-law", label: "Governing Law" },
    { id: "contact", label: "Contact Information" },
  ];

  const faqs = [
    {
      q: "What am I agreeing to by using TeamGrid?",
      a: "By using TeamGrid, you agree to the terms governing access, data usage, reporting, and service conditions including billing, user rules, limitations, and rights outlined in the Terms & Conditions.",
    },
    {
      q: "Where can I find contact info for legal or support questions?",
      a: "For legal or support questions, contact us at hello@teamgrid.ai or visit www.teamgrid.ai. Support: support@teamgrid.ai.",
    },
  ];

  return (
      <>
      <Helmet>
        <title>Terms of Service | TeamGrid</title>
        <meta name="description" content="TeamGrid terms of service. Eligibility, service description, subscriptions, and acceptable use." />
        <link rel="canonical" href="https://teamgrid.ai/terms-of-service" />
      </Helmet>
      <div className="terms-container">
          <PublicHeader />
      <div className="pt-16 sm:pt-18 md:pt-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="terms-main max-w-[1134px] mx-auto py-[80px] flex flex-col gap-[47px]">
          {/* Hero - spec: border 1px #00D1C1, bg rgba(0,0,0,0.05), radius ~24px */}
          <div className="terms-hero">
            <h1 className="terms-hero-title">Terms & Conditions for <span style={{ color: TEAL }}>TeamGrid</span></h1>
            <p className="terms-hero-intro">
              These Terms & Conditions ("Terms") govern your access to and use of TeamGrid, our websites, desktop apps, and related services (collectively, the "Service"). By creating an account, installing the desktop agent, or using TeamGrid in any way, you agree to these Terms on behalf of yourself and any organization you represent.
            </p>
            <div className="terms-hero-pills">
              <div className="terms-pill">
                <Clock className="w-5 h-5 flex-shrink-0" style={{ color: TEAL }} />
                <span><strong>Last updated:</strong></span>
                <span>27 May 2026</span>
                          </div>
              <div className="terms-pill">
                <UsersIcon className="w-5 h-5 flex-shrink-0" style={{ color: TEAL }} />
                <span>Applies to:</span>
                <span>Admins, Managers & Team Members</span>
                        </div>
                      </div>
                    </div>

                  {/* Quick Navigation */}
          <div className="terms-quick-nav">
            <div className="terms-quick-nav-header">
              <div className="terms-quick-nav-icon">
                <Menu className="w-6 h-6" style={{ color: TEAL }} />
              </div>
              <h3 className="terms-quick-nav-title">Quick Navigation</h3>
                        </div>
            <div className="terms-quick-nav-grid">
              {navItems.map((item) => (
                              <button
                                key={item.id}
                  type="button"
                                onClick={() => scrollToSection(item.id)}
                  className="terms-quick-nav-link"
                              >
                  <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: TEAL }} />
                                <span>{item.label}</span>
                              </button>
              ))}
                      </div>
                    </div>

          {/* Sections 1–16 */}
          <TermsSection id="introduction" number={1} title="Introduction">
            <p>TeamGrid is operated by Cord4 Technologies (referred to as "TeamGrid," "we," "us," or "our"). These Terms form a legal agreement between you and TeamGrid. If you do not agree with any part of these Terms, you must not use the Service.</p>
          </TermsSection>

          <TermsSection id="eligibility" number={2} title="Eligibility & Accounts">
            <h4 className="terms-h4">2.1 Eligibility</h4>
            <p>You may use TeamGrid only if you are legally capable of entering into a binding contract under the laws of your jurisdiction and are authorized to act on behalf of your organization or employer.</p>
            <h4 className="terms-h4">2.2 Account Registration</h4>
            <p>To access the Service, your organization will create a workspace and user accounts. You must provide accurate information and keep it updated. You are responsible for maintaining the confidentiality of your login credentials and all activities occurring under your account.</p>
            <h4 className="terms-h4">2.3 Admin & Manager Roles</h4>
            <p>Workspace owners, admins, and managers may configure settings, invite users, assign roles, and view aggregated team insights. Your use of this data must comply with applicable employment, labor, and privacy laws.</p>
          </TermsSection>

          <TermsSection id="service" number={3} title="Service Description">
            <p>TeamGrid provides analytics and AI-generated insights about work patterns, including time spent on applications, projects, and activities, as well as daily and monthly summaries and organizational reports.</p>
            <ul className="terms-list">
              <li>Real-time activity and productivity dashboards</li>
              <li>Daily and monthly AI summaries for individual team members</li>
              <li>Work pattern analysis, strengths, issues, and improvement suggestions</li>
              <li>Org Intelligence and team hierarchy mapping</li>
              <li>Project-based time allocation and performance reports</li>
              <li>Automation recommendations for repetitive manual tasks</li>
                        </ul>
            <p>We may modify, improve, or discontinue parts of the Service from time to time. Where changes are material, we will make reasonable efforts to notify workspace owners and admins.</p>
          </TermsSection>

          <TermsSection id="work-tracking" number={4} title="Work Tracking & Privacy">
            <p>TeamGrid is designed to provide "signal, not surveillance." The Service focuses on applications, projects, and work patterns rather than capturing private content.</p>
            <ul className="terms-list">
              <li>The desktop agent tracks active time, apps and websites used, and context needed to build work summaries.</li>
              <li>We do not record what you type or passwords and do not intentionally capture private content.</li>
              <li>Screenshots, if enabled by your organization, are handled according to workspace settings and our Privacy Policy.</li>
                        </ul>
            <p>Use of TeamGrid is also governed by our <Link to="/privacy-policy" className="terms-link">Privacy Policy</Link>, which describes how we collect, process, and protect data. In case of any conflict between these Terms and the Privacy Policy, these Terms prevail for contractual matters.</p>
          </TermsSection>

          <TermsSection id="subscriptions" number={5} title="Subscriptions, Plans & Payments">
            <p>TeamGrid may be offered as a free plan and one or more paid subscription plans based on the number of users and features.</p>
            <ul className="terms-list">
              <li>Fees, billing cycles, and plan limits are described on <a href="https://www.teamgrid.ai" target="_blank" rel="noopener noreferrer" className="terms-link">www.teamgrid.ai</a>.</li>
              <li>Subscription charges are typically billed in advance and are non-refundable except where required by law.</li>
              <li>Workspace owners are responsible for all charges associated with their account, including invited users.</li>
                        </ul>
            <p>We may change pricing or introduce new charges by providing reasonable notice to workspace owners or by updating publicly available pricing information.</p>
          </TermsSection>

          <TermsSection id="admin" number={6} title="Organization & Admin Responsibilities">
            <p>Your organization controls how TeamGrid is used internally. Admins decide which teams, roles, and users are added and which features are enabled.</p>
            <ul className="terms-list">
              <li>You are responsible for configuring TeamGrid in line with your internal policies and legal obligations.</li>
              <li>You must inform employees and team members that TeamGrid is being used and what data is collected.</li>
              <li>You are responsible for any actions taken using your workspace, including automation changes or HR decisions based on insights.</li>
                        </ul>
          </TermsSection>

          <TermsSection id="acceptable-use" number={7} title="Acceptable Use">
            <p>You agree not to misuse TeamGrid. This includes, but is not limited to:</p>
            <ul className="terms-list">
              <li>Using the Service to illegally monitor individuals or violate privacy, labor, or employment laws.</li>
              <li>Attempting to reverse engineer, decompile, or copy any part of the Service.</li>
              <li>Interfering with or disrupting the integrity or performance of the Service.</li>
              <li>Uploading malicious code, attempting unauthorized access, or breaching security features.</li>
              <li>Using insights from TeamGrid to discriminate or harass individuals.</li>
                        </ul>
          </TermsSection>

          <TermsSection id="intellectual-property" number={8} title="Intellectual Property">
            <p>TeamGrid, including its software, user interface, logos, content, and documentation, is owned by us or our licensors and is protected by copyright, trademark, and other laws.</p>
            <ul className="terms-list">
              <li>We grant you a limited, non-exclusive, non-transferable license to access and use the Service for your internal business purposes.</li>
              <li>You retain ownership of data you upload or generate within your workspace. We process that data solely to provide and improve the Service.</li>
                        </ul>
          </TermsSection>

          <TermsSection id="google-api" number={9} title="Google API Services & Email Insights">
            <p>TeamGrid's Email Insights feature integrates with Google API Services to provide email productivity analytics. We use the <strong>auth/gmail.metadata</strong> scope only — one of Google's most restricted Gmail scopes, which does <em>not</em> grant access to message bodies or attachments. While the <code>gmail.metadata</code> scope technically provides access to subject lines, TeamGrid explicitly does not fetch, process, or store subject lines in any form. By connecting your Google account, you agree to the following:</p>

            <h4 className="terms-h4">9.1 Data We Access</h4>
            <p>Our access to Gmail is strictly <strong>metadata-only</strong>. We never send, modify, or delete any of your emails — and we never access their content. With your explicit consent, we access only:</p>
            <ul className="terms-list">
              <li><strong>Sender and recipient addresses</strong> — the From / To headers.</li>
              <li><strong>Timestamps</strong> — when each message was sent or received.</li>
              <li><strong>Message headers (metadata only)</strong> — routing/delivery headers such as From, To, Date, Message-ID, and In-Reply-To. We never access the Subject header or any body content.</li>
              <li><strong>Gmail labels</strong> — e.g. INBOX, SENT, used to classify direction and skip spam/trash/drafts.</li>
              <li><strong>Thread and message identifiers</strong> — opaque IDs used to reconstruct reply chains and communication loops.</li>
            </ul>
            <p>While the <code>gmail.metadata</code> scope technically provides access to subject lines, TeamGrid explicitly does not fetch, process, or store subject lines in any form. The scope does not grant access to message bodies, attachments, or any other email content.</p>

            <h4 className="terms-h4">9.2 How We Use Your Data</h4>
            <p>Your Gmail metadata is used solely to power the following Email Insights features inside TeamGrid:</p>
            <ul className="terms-list">
              <li><strong>Volume & trend analytics</strong> — sent vs. received counts, day-of-week and time-of-day heatmaps.</li>
              <li><strong>Response-time analysis</strong> — average and median reply latency, distribution buckets, delayed-response detection.</li>
              <li><strong>Top-contact tables</strong> — most active senders and recipients by email count.</li>
              <li><strong>Communication-loop tracking</strong> — pending vs. closed conversations and which side is awaiting a reply (using thread/message IDs only — never message content).</li>
            </ul>
            <p>We do <strong>not</strong> use your Gmail metadata for advertising, profiling, or any purpose unrelated to these features. We do <strong>not</strong> use your Gmail metadata — in any form — to train AI or machine learning models, neither ours nor any third party's. We do <strong>not</strong> sell, rent, or share your Gmail data with third parties, except secure cloud infrastructure providers strictly necessary to operate the Service.</p>

            <h4 className="terms-h4">9.3 Data Storage, Processing & Hosting</h4>
            <ul className="terms-list">
              <li>Gmail metadata is processed in real time through secure, encrypted API calls and is <strong>not</strong> permanently stored on our servers.</li>
              <li>No subject lines, message bodies, or attachments are ever fetched, processed, transmitted, logged, or stored by TeamGrid.</li>
              <li>Any temporarily cached metadata is encrypted at rest using <strong>AES-256</strong>, refreshed or discarded on a short rolling window.</li>
              <li>Google OAuth tokens (access and refresh tokens) are stored encrypted using AES-256-GCM in our secure database.</li>
              <li>All processing of Gmail metadata occurs on infrastructure hosted in the <strong>United States and the European Union</strong>.</li>
              <li>Data in transit is protected with TLS 1.3.</li>
            </ul>

            <h4 className="terms-h4">9.4 Revoking Access & Data Deletion</h4>
            <ul className="terms-list">
              <li>You can disconnect your Google account at any time from within the Email Insights settings in TeamGrid.</li>
              <li>Upon disconnection, we immediately revoke the OAuth tokens and delete all stored tokens and cached Gmail metadata associated with your account.</li>
              <li>You may also revoke TeamGrid's access directly from your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="terms-link">Google Account Permissions</a> page.</li>
            </ul>

            <h4 className="terms-h4">9.5 Compliance</h4>
            <p>Our use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="terms-link">Google API Services User Data Policy</a>, including the Limited Use requirements. We follow the principle of least privilege — we request a highly restricted, read-only Gmail scope (<code>gmail.metadata</code>); although that scope technically exposes subject lines, we deliberately never fetch, process, or store them, we never read message bodies or attachments, and we never modify, send, or delete any user emails.</p>
          </TermsSection>

          <TermsSection id="third-party" number={10} title="Third-Party Services">
            <p>TeamGrid may integrate with third-party tools (for example, project management systems or communication platforms). Your use of third-party services is governed by their own terms and policies.</p>
            <p>We are not responsible for third-party services or for any damage or loss arising from your use of them in connection with TeamGrid.</p>
          </TermsSection>

          <TermsSection id="warranties" number={11} title="Disclaimer of Warranties">
            <p>The Service is provided on an "AS IS" and "AS AVAILABLE" basis. To the fullest extent permitted by law, we disclaim all warranties, express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</p>
            <p>We do not guarantee that the Service will be uninterrupted, secure, or error-free, or that insights will be perfectly accurate in every scenario. TeamGrid is a decision-support tool and not a substitute for human judgment.</p>
          </TermsSection>

          <TermsSection id="liability" number={12} title="Limitation of Liability">
            <p>To the maximum extent permitted by law, TeamGrid and its affiliates are not liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or business opportunities.</p>
            <p>Our total aggregate liability for any claim arising out of or related to your use of the Service will not exceed the amounts paid by your organization to us for the Service in the twelve (12) months preceding the event giving rise to the claim.</p>
          </TermsSection>

          <TermsSection id="indemnification" number={13} title="Indemnification">
            <p>You agree to indemnify and hold TeamGrid, its officers, employees, and partners harmless from any claims, losses, damages, or expenses (including reasonable legal fees) arising from:</p>
            <ul className="terms-list">
              <li>Your misuse of the Service;</li>
              <li>Your violation of these Terms; or</li>
              <li>Any employment or contractual dispute between you and your team members related to the use of TeamGrid.</li>
                        </ul>
          </TermsSection>

          <TermsSection id="termination" number={14} title="Suspension & Termination">
            <p>We may suspend or terminate your access to the Service if you breach these Terms, use TeamGrid in a way that creates legal risk, or harm the security, stability, or reputation of the Service.</p>
            <p>Workspace owners may deactivate users or cancel the workspace at any time. Upon termination, your right to use the Service will stop, but certain provisions (including intellectual property, payment obligations, and limitations of liability) will continue to apply.</p>
          </TermsSection>

          <TermsSection id="changes" number={15} title="Changes to These Terms">
            <p>We may update these Terms from time to time. When we make material changes, we will update the "Last updated" date above and, where appropriate, notify workspace owners by email or in-app message.</p>
            <p>Your continued use of the Service after any changes become effective constitutes acceptance of the revised Terms.</p>
          </TermsSection>

          <TermsSection id="governing-law" number={16} title="Governing Law & Dispute Resolution">
            <p>These Terms are governed by the laws applicable in the jurisdiction in which TeamGrid is registered, without regard to conflict of law principles.</p>
            <p>Any dispute arising out of or relating to these Terms or the Service will first be attempted to be resolved amicably. If no resolution is reached, the dispute shall be submitted to the competent courts of that jurisdiction, unless otherwise required by mandatory local law.</p>
          </TermsSection>

          <TermsSection id="contact" number={17} title="Contact Information">
            <p>If you have questions about these Terms, your account, or the Service, you can reach us using the details below.</p>
            <ul className="terms-list">
              <li><strong>Support:</strong> <a href="mailto:support@teamgrid.ai" className="terms-link">support@teamgrid.ai</a> — for help with your account, billing, or technical issues.</li>
              <li><strong>Business & legal:</strong> <a href="mailto:hello@teamgrid.ai" className="terms-link">hello@teamgrid.ai</a> — for partnership, legal, privacy, or data-deletion requests.</li>
              <li><strong>Website:</strong> <a href="https://www.teamgrid.ai" target="_blank" rel="noopener noreferrer" className="terms-link">www.teamgrid.ai</a></li>
            </ul>
            <p>TeamGrid is operated by Cord4 Technologies.</p>
          </TermsSection>

                    </div>

        {/* FAQ Section */}
        <section className="terms-faq-section py-20 px-6 flex flex-col items-center" style={{ background: "rgba(0, 0, 0, 0.05)" }}>
          <div className="max-w-[1200px] w-full flex flex-col items-center gap-16">
            <div className="flex flex-col items-center gap-5 text-center">
              <div
                className="terms-faq-badge inline-flex items-center justify-center border px-5 py-2"
                style={{ borderRadius: 100, background: "rgba(0, 209, 193, 0.06)", borderColor: "rgba(0, 209, 193, 0.15)" }}
              >
                <span className="font-semibold uppercase tracking-wide text-sm" style={{ letterSpacing: "0.72px", color: TEAL }}>Got Questions?</span>
                          </div>
              <h2 className="font-extrabold text-black" style={{ fontSize: 48, lineHeight: "82px", letterSpacing: "-0.96px" }}>Frequently Asked Questions</h2>
              <p className="text-black/60" style={{ fontSize: 24, lineHeight: "43px", maxWidth: 930 }}>Everything you need to know about how TeamGrid works and the value it delivers.</p>
                        </div>
            <div className="w-full max-w-[1053px] flex flex-col gap-5">
              {faqs.map((faq, i) => (
                <div key={i} className="terms-faq-card overflow-hidden">
                  <button type="button" onClick={() => toggleFaq(i)} className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-6 text-left">
                    <span className="font-medium text-black text-[23px] leading-[35px]">{faq.q}</span>
                    <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 text-2xl font-light text-black">
                      {faqOpen[i] ? "−" : "+"}
                    </span>
                  </button>
                  {faqOpen[i] && (
                    <div className="px-6 sm:px-8 pb-6 pt-0">
                      <p className="text-black text-[20px] leading-[33px]">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
                </div>
              </section>
        </div>

        <ReadyToTransform />
        <Footer />
      </div>

              {showScrollTop && (
                <motion.button
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 rounded-xl flex items-center justify-center z-50"
          style={{ background: TEAL, boxShadow: "0px 4px 12px rgba(0, 209, 193, 0.3)" }}
                >
          <ArrowUp className="w-6 h-6 text-black" strokeWidth={2} />
                </motion.button>
              )}
            </div>
      </>
  );
};

export default Terms;
